import { FC, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Semibold, Subtitle } from "capitalrx-components-2.0/lib/typography";

import { formatDollars } from "@/App/utils/utils";
import styled from "styled-components";
import "./PaymentDetails.scss";
import { Color, Icon, Tooltip } from "capitalrx-components-2.0/lib/general";

const PaymentBar = styled.div<{ color: string, widthpercent: string }>`
  background-color: ${(props) => props.color};
  width: calc(${(props) => props.widthpercent}%);
  max-width: 100%;
  height: 35px;
`;

interface PaymentDetailsProps {
  readonly patientPayAmount: number;
  readonly planPayAmount: number;
  readonly copayCoinsuranceAmount?: number;
  readonly usageContext: "claim" | "fbp";
}

interface PaymentBarEntry {
  quantity: number;
  color: string;
  tooltip: ReactNode;
}

const PaymentBars: FC<{ entries: PaymentBarEntry[] }> = ({ entries }) => {
  const total = entries.reduce((total, entry) => total + entry.quantity, 0);

  if (Number.isFinite(total) && Math.abs(total) < 0.005) {
    return <></>;
  }

  return <Shelf spacing="mini">
    {entries.map((entry, idx) => (
      <Tooltip
        alignment="top"
        content={entry.tooltip}
        key={"PaymentBar" + idx}
      >
        {(ref) => <PaymentBar widthpercent={"" + (entry.quantity / total * 100)} color={entry.color} ref={ref} />}
      </Tooltip>
    ))}
  </Shelf>;
};

export const PaymentDetails: FC<PaymentDetailsProps> = ({ patientPayAmount, planPayAmount, copayCoinsuranceAmount = 0, usageContext }) => {
  const { t: translate } = useTranslation();
  const formattedPatientPayAmount = formatDollars(patientPayAmount || "0");
  const formattedPlanPayAmount = formatDollars(planPayAmount || "0");
  const formattedCopayCoinsurancePayAmount = formatDollars(copayCoinsuranceAmount || "0");
  const formattedNotCoveredAmount = formatDollars(patientPayAmount - copayCoinsuranceAmount);

  const entries = useMemo(() => {
    const entries = [];

    if (copayCoinsuranceAmount >= 0.005) {
      entries.push({
        color: Color.toString(Color.Turqoise["400"]),
        quantity: copayCoinsuranceAmount,
        tooltip: (<span>
          {translate("PRICING_DETAIL_PANELS.paymentDetails.copaySlashCoinsurance")}: <Semibold>{formattedCopayCoinsurancePayAmount}</Semibold>
        </span>),
      });
    }

    const notCoveredAmount = patientPayAmount - copayCoinsuranceAmount;
    if (notCoveredAmount >= 0.005) {
      entries.push({
        color: Color.toString(Color.Turqoise["600"]),
        quantity: notCoveredAmount,
        tooltip: (<span>
          {translate("PRICING_DETAIL_PANELS.paymentDetails.youPaid")}: <Semibold>{formattedNotCoveredAmount}</Semibold>
        </span>),
      });
    }

    if (planPayAmount >= 0.005) {
      entries.push({
        color: Color.toString(Color.Green["500"]),
        quantity: planPayAmount,
        tooltip: (<span>
          {translate("PRICING_DETAIL_PANELS.paymentDetails.planPaid")}: <Semibold>{formattedPlanPayAmount}</Semibold>
        </span>),
      });
    }

    return entries;
  }, [patientPayAmount, planPayAmount, copayCoinsuranceAmount]);

  return (
    <Stack>
      <Grid spacing="none">
        <Grid.Span columns={12}>
          <Shelf spacing="small">
            <div className="leftLine">
              <div />
            </div>
            <Stack spacing="none" className="leftStack">
              {usageContext === "fbp" && <Semibold>{translate("PRICING_DETAIL_PANELS.paymentDetails.youWouldPay")}</Semibold>}
              {usageContext === "claim" && (
                <Shelf spacing="mini" alignment="center">
                  <Semibold>{translate("PRICING_DETAIL_PANELS.paymentDetails.youPaid")}</Semibold>
                  <Tooltip
                    content={
                      <span>
                        {translate("PRICING_DETAIL_PANELS.paymentDetails.copaySlashCoinsurance")}: <Semibold>{formattedCopayCoinsurancePayAmount}</Semibold>
                        <br />
                        {translate("PRICING_DETAIL_PANELS.paymentDetails.amountNotCovered")}: <Semibold>{formattedNotCoveredAmount}</Semibold>
                      </span>
                    }
                    alignment="top"
                  >
                    {(ref) => (
                      <div ref={ref} style={{ display: "flex" }}>
                        <Icon name="info-circle" />
                      </div>
                    )}
                  </Tooltip>
                </Shelf>
              )}
              <Subtitle testId="patient-pay-amount" textAlign="right" className="patient-pay">
                {formattedPatientPayAmount}
              </Subtitle>
            </Stack>
            <div className="midContainer">
              <div />
            </div>
            <Stack spacing="none" className="rightStack">
              {usageContext === "fbp" && <Semibold>{translate("PRICING_DETAIL_PANELS.paymentDetails.planWouldPay")}</Semibold>}
              {usageContext === "claim" && <Semibold>{translate("PRICING_DETAIL_PANELS.paymentDetails.planPaid")}</Semibold>}
              <Subtitle testId="plan-pay-amount" className="plan-pay">{formattedPlanPayAmount}</Subtitle>
            </Stack>
            <div className="rightLine">
              <div />
            </div>
          </Shelf>
        </Grid.Span>

        <Grid.Span columns={12}>
          <PaymentBars entries={entries} />
        </Grid.Span>
      </Grid>
    </Stack>
  );
};
